/**
 * W13 DM2 — the bridge between the evidence surfaces and the ONE client reader
 * of a pinned analysis (`firebase-core/InsightThesisService.listEvidence`).
 *
 * Two consumers: the screen inspector's «Why this copy» panel
 * (`reactFlow/editorSectionsV2/AiMetaSection`) and the insight-impact dialog.
 * Both hand it the citation paths they are about to render; it reads each named
 * analysis exactly once and returns the index the viewer resolves against.
 *
 * Failure is never silent and never fatal: `error` is set, `byAnalysis` stays
 * without that analysis, and the panel falls back to the raw citation path with
 * the «could not be read» note (AC-W13-09). Per ANALYSIS (final review FR-7): one
 * unreadable analysis never takes the rows of the ones that loaded with it.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { InsightThesisService } from '@modules/firebase-core/services/InsightThesisService';
import { parseInsightCitationPath } from '@modules/strategy-blocks/utils/insightCitationPath';
import {
  buildInsightEvidenceIndex,
  type InsightEvidenceIndex,
  type InsightEvidenceState,
} from './insightEvidence';

/** The analysis ids a list of citations names, deduplicated, in first order. */
export function citedAnalysisIds(sourcePaths: Iterable<unknown>): string[] {
  const ids: string[] = [];
  for (const path of sourcePaths) {
    const parsed = parseInsightCitationPath(path);
    if (parsed && !ids.includes(parsed.analysisId)) ids.push(parsed.analysisId);
  }
  return ids;
}

export interface UseInsightEvidenceOptions {
  projectId?: string;
  /** Every `sourcePath` the surface is about to render. */
  sourcePaths: unknown[];
}

export function useInsightEvidence({
  projectId,
  sourcePaths,
}: UseInsightEvidenceOptions): InsightEvidenceState {
  const analysisIds = useMemo(() => citedAnalysisIds(sourcePaths), [sourcePaths]);
  const key = analysisIds.join('|');

  const [state, setState] = useState<{ byAnalysis: InsightEvidenceIndex; loading: boolean; error: string | null }>(
    { byAnalysis: {}, loading: false, error: null },
  );
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!projectId || analysisIds.length === 0) {
      setState({ byAnalysis: {}, loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState({ byAnalysis: {}, loading: true, error: null });
    const service = new InsightThesisService(projectId);
    /**
     * Final review FR-7: `allSettled`, not `all`. A screen can cite two analyses (a block
     * kept from an earlier generation next to a regenerated one), and one unreadable
     * analysis used to drop the one that DID load — every row on the panel then said «the
     * pinned analysis could not be read», including the rows whose evidence was in hand.
     * Each analysis now answers for itself: the loaded ones render, the failed ones are
     * absent from the index and get the «could not be read» note through
     * `missingAnalysisNote` (the resolver's existing, per-row behaviour).
     */
    Promise.allSettled(analysisIds.map(async (analysisId) => {
      const rows = await service.listEvidence(analysisId);
      return [analysisId, buildInsightEvidenceIndex(rows)] as const;
    }))
      .then((results) => {
        if (cancelled || !mounted.current) return;
        const byAnalysis = Object.fromEntries(results
          .filter((result): result is PromiseFulfilledResult<readonly [string, InsightEvidenceIndex[string]]> =>
            result.status === 'fulfilled')
          .map((result) => result.value));
        const failures = results.filter((result) => result.status === 'rejected') as PromiseRejectedResult[];
        setState({
          byAnalysis,
          loading: false,
          // The error line still names a failure — a reader that shows it (and the rows of
          // the analyses that loaded) is told the truth about both halves.
          error: failures.length === 0
            ? null
            : (failures[0].reason instanceof Error
              ? failures[0].reason.message
              : 'The pinned analysis could not be read'),
        });
      });
    return () => { cancelled = true; };
    // `key` is the stable identity of `analysisIds` (a fresh array each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, key]);

  return state;
}
